/**
 * Block Editor — Phase 3.
 *
 * Loads either:
 *   - kind=seed → fork-on-first-save (POST .../seed/:id/fork creates the fork,
 *     navigates to the new fork id so the URL becomes stable).
 *   - kind=fork → load the existing fork for editing.
 *
 * Save semantics: client sends the full edited state (name, description, rows
 * array) on each save. Server REPLACEs rows wholesale and bumps version.
 *
 * Actions:
 *   - Save Changes
 *   - Reset to CrewForge Defaults (only for forks linked to a seed)
 *   - Delete (only for forks; soft-deletes and the seed re-appears in Library)
 *   - Back to Library
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import remountOnRouteParams from '../utils/remountOnRouteParams';
import { BTN } from '../constants/buttons';
import HrnInput from '../components/HrnInput';
import ControlsByStage from '../components/ControlsByStage';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { CATEGORY_OPTIONS } from '../constants/riskAssessment';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

function emptyRow() {
  return {
    hazard: '',
    who_affected: '',
    lo_init: 1, fe_init: 1, dph_init: 0.1, np_init: 1,
    controls: '',
    lo_res: 1, fe_res: 1, dph_res: 0.1, np_res: 1,
    notes: '',
  };
}

// One instance per block route: four routes render this, and `forkId` is seeded
// from the params only at mount, so Back/Forward onto another block kept the old
// fork id and a Save wrote this block's content into it (R14 review).
export default remountOnRouteParams(
  RiskAssessmentBlockEditor,
  (params, props) => `${props.seedMode ? 'seed' : 'prod'}:${params.kind || ''}:${params.id || 'new'}`,
);

function RiskAssessmentBlockEditor({ seedMode = false }) {
  const navigate = useNavigate();
  const { kind, id } = useParams();
  // seedMode: admin-only CrewForge platform-library authoring (writes
  // seed_blocks, not ra_blocks). When true: no production needed, no fork
  // step, no reset-to-defaults (no parent seed). Uses POST/PUT /seed-blocks
  // endpoints instead of /blocks. URL conventions:
  //   - /risk-assessments/library/seed-blocks/new       → create new seed
  //   - /risk-assessments/library/seed-blocks/edit/:id  → edit existing seed
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState([]);
  const [seedSlug, setSeedSlug] = useState(null);
  const [forkId, setForkId] = useState(kind === 'fork' ? parseInt(id, 10) : null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // New bespoke block (per-prod) OR new seed (admin) — start blank.
      if (!id) {
        setName('');
        setDescription('');
        setCategory('');
        setRows([emptyRow()]);
        setSeedSlug(null);
        setIsDirty(true);
        setLoading(false);
        return;
      }
      // Seed-mode edit: load the seed directly via /blocks/seed/:id (the
      // existing read endpoint already serves seed_blocks rows for any
      // authed user).
      const endpoint = seedMode
        ? `/api/risk-assessments/blocks/seed/${id}`
        : `/api/risk-assessments/blocks/${kind}/${id}`;
      const res = await apiGet(endpoint);
      if (!res.ok) throw new Error('Failed to load block');
      const data = await res.json();
      setName(data.name || '');
      setDescription(data.description || '');
      setCategory(data.category || '');
      setRows((data.rows || []).map((r) => ({ ...r })));
      setSeedSlug(data.seed_slug || (kind === 'seed' || seedMode ? data.slug : null));
      setIsDirty(false);
    } catch (err) {
      setError(err.message || 'Failed to load block');
    }
    setLoading(false);
  }, [kind, id, seedMode]);

  useEffect(() => { loadContent(); }, [loadContent]);

  // Warn before leaving with unsaved changes — in-app navigation, Back, reload,
  // tab close (R14, 2026-09-15; `hooks/useUnsavedChangesGuard.js`; was a bare
  // `beforeunload`). 🔑 Every save and delete below navigates AFTER an await
  // that follows `setIsDirty(false)`, before React has re-rendered — so each
  // calls `allowNextNavigation()` first, or the guard would hold the save's
  // own redirect and then drop it when the flag clears.
  // `isDirty` is forced true on a BLANK new block (it enables Save), so an
  // untouched blank form — no id, nothing typed, the one empty starter row —
  // is excluded: prompting over nothing trains people to click Leave.
  const untouchedNewBlock = !id && !forkId && name.trim() === '' && description === '' && category === ''
    && rows.length <= 1 && JSON.stringify(rows[0] || emptyRow()) === JSON.stringify(emptyRow());
  const leaveGuard = useUnsavedChangesGuard({
    when: isDirty && !untouchedNewBlock,
    message: "Your changes to this block haven't been saved. If you leave, they will be lost.",
  });

  const markDirty = () => setIsDirty(true);

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    markDirty();
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
    markDirty();
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    markDirty();
  };

  const moveRow = (idx, direction) => {
    setRows((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    markDirty();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    // Safety prompt before writing to the platform-wide CrewForge library.
    // Guards against the "I forgot I'm logged in as admin" footgun — these
    // changes ripple out to every user on every production immediately.
    if (seedMode) {
      const intro = !id
        ? 'This new CrewForge hazard block will be visible to EVERY user on EVERY production as soon as it saves.'
        : 'These changes to a CrewForge hazard block will be visible to EVERY user on EVERY production as soon as they save.';
      if (!window.confirm(
        `⚠ Editing the platform-wide CrewForge Library Block\n\n${intro}\n\n`
        + 'If you only meant to change your own block, cancel and switch '
        + 'to an individual user account first.\n\nContinue saving?'
      )) {
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      let editId = forkId;

      // Seed-mode (admin platform-library) — writes to seed_blocks directly.
      if (seedMode) {
        if (!id) {
          // New CrewForge default block
          const res = await apiPost('/api/risk-assessments/seed-blocks', {
            name, description, rows, category: category || null,
          });
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.error || 'Failed to create CrewForge default block');
          }
          const data = await res.json();
          setIsDirty(false);
          leaveGuard.allowNextNavigation();
          navigate(`/risk-assessments/library/seed-blocks/edit/${data.id}`, { replace: true });
        } else {
          // Edit existing CrewForge default block
          const res = await apiPut(`/api/risk-assessments/seed-blocks/${id}`, {
            name, description, rows, category: category || null,
          });
          if (!res.ok) {
            const body = await res.json().catch(() => null);
            throw new Error(body?.error || 'Failed to save CrewForge default block');
          }
          setIsDirty(false);
          await loadContent();
        }
      } else if (!kind && !id) {
        // Bespoke new: POST /blocks creates the new ra_block + rows in one shot.
        const res = await apiPost('/api/risk-assessments/blocks', {
          name, description, rows, category: category || null,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || 'Failed to create block');
        }
        const data = await res.json();
        editId = data.id;
        setForkId(editId);
        setIsDirty(false);
        leaveGuard.allowNextNavigation();
        navigate(`/risk-assessments/library/blocks/edit/fork/${editId}`, { replace: true });
      } else {
        // Editing a seed (read-only) — fork it first, then write.
        if (kind === 'seed' && !editId) {
          const forkRes = await apiPost(`/api/risk-assessments/blocks/seed/${id}/fork`, {});
          if (!forkRes.ok) throw new Error('Failed to fork seed');
          const forkData = await forkRes.json();
          editId = forkData.id;
          setForkId(editId);
        }

        const res = await apiPut(`/api/risk-assessments/blocks/fork/${editId}`, {
          name, description, rows, category: category || null,
        });
        if (!res.ok) throw new Error('Failed to save changes');

        setIsDirty(false);
        // Land on the fork URL so subsequent saves don't re-fork. 🔑 (R14 review
        // M-c) When the URL CHANGES (a seed was just forked) the route key changes
        // and a fresh instance loads the fork itself — refetching here would run on
        // the unmounted old instance and GET seed/3 for nobody. Only an edit of an
        // existing fork, whose URL does not move, reloads in place.
        if (kind === 'fork' && String(id) === String(editId)) {
          await loadContent();
        } else {
          leaveGuard.allowNextNavigation();
          navigate(`/risk-assessments/library/blocks/edit/fork/${editId}`, { replace: true });
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save block');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!forkId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiPost(`/api/risk-assessments/blocks/fork/${forkId}/reset-to-defaults`, {});
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to reset to defaults');
      }
      setConfirmReset(false);
      await loadContent();
    } catch (err) {
      setError(err.message || 'Failed to reset');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    // Strong warning before deleting from the platform-wide CrewForge library.
    // Matches the save-flow popup — same footgun (admin forgets they're not on
    // their own account, takes the whole library down with one click).
    if (seedMode) {
      if (!window.confirm(
        `⚠ Deleting from the platform-wide CrewForge Library Block\n\n`
        + `The CrewForge hazard block "${name}" will disappear from EVERY user's library on EVERY production on next page load.\n\n`
        + `If you only meant to delete your own block, cancel and switch to an individual user account first.\n\nContinue?`
      )) {
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      // seedMode: deletes the CrewForge default itself (writes seed_blocks).
      // Per-prod fork (default): deletes the user's ra_blocks fork.
      if (seedMode) {
        if (!id) return;
        const res = await apiDelete(`/api/risk-assessments/seed-blocks/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          // 409 block_in_use: server returns the list of templates that
          // reference this block so the admin knows what to disconnect first.
          if (res.status === 409 && body?.code === 'block_in_use') {
            const names = (body.templates || []).map((t) => `• ${t.name}`).join('\n');
            throw new Error(`${body.error}\n\nReferenced by:\n${names}`);
          }
          throw new Error(body?.error || 'Failed to delete CrewForge default block');
        }
        leaveGuard.allowNextNavigation();
        navigate('/risk-assessments/library', { replace: true });
      } else {
        if (!forkId) return;
        const res = await apiDelete(`/api/risk-assessments/blocks/fork/${forkId}`);
        if (!res.ok) throw new Error('Failed to delete block');
        leaveGuard.allowNextNavigation();
        navigate('/risk-assessments', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Failed to delete');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8"><LoadingSpinner /></div>;

  const isForkOfSeed = !!(forkId && seedSlug);
  const isBespoke = !!(forkId && !seedSlug);
  const isViewingSeed = kind === 'seed' && !forkId;
  const isNewBespoke = !kind && !id && !forkId;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full mx-auto pb-32">
      {leaveGuard.dialog}
      <header className="mb-4 flex items-start gap-3 flex-wrap">
        <button
          // The leave guard asks now; a window.confirm here as well would ask twice.
          onClick={() => navigate('/risk-assessments')}
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          data-testid="ra-editor-back"
        >
          ← Library
        </button>
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isViewingSeed && 'Customising: '}
            {isNewBespoke ? 'New bespoke block' : (name || 'Untitled block')}
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {isViewingSeed && 'Changes will create a fork in your department. The CrewForge default remains untouched.'}
            {isForkOfSeed && 'Edits stay in your department. You can reset to CrewForge defaults at any time.'}
            {isBespoke && 'Bespoke block — no default to reset to.'}
            {isNewBespoke && 'Bespoke block — not based on a CrewForge default. Saves to your department library.'}
          </p>
        </div>
      </header>

      {error && <ErrorMessage error={error} />}

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); markDirty(); }}
            className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="ra-editor-name"
          />
        </label>
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => { setDescription(e.target.value); markDirty(); }}
            className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="ra-editor-description"
          />
        </label>
        <label className="block">
          <span className={FIELD_LABEL_CLASS}>
            Category
            <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">(controls tree grouping)</span>
          </span>
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value); markDirty(); }}
            className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
            data-testid="ra-editor-category"
          >
            <option value="">— Uncategorised —</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
        </label>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Hazard rows</h2>
        {rows.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No hazard rows yet. Add one to get started.</p>
        )}
        <div className="space-y-3">
          {rows.map((r, idx) => {
            const initial = { lo: r.lo_init, fe: r.fe_init, dph: r.dph_init, np: r.np_init };
            const residual = { lo: r.lo_res,  fe: r.fe_res,  dph: r.dph_res,  np: r.np_res };
            return (
              <div
                key={r.id || `new:${idx}`}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                data-testid={`ra-editor-row-${idx}`}
              >
                <div className="flex items-start gap-2 mb-2">
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300 shrink-0">
                    {idx + 1}
                  </span>
                  <div className="flex-1 grid gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Hazard</span>
                      <input
                        type="text"
                        value={r.hazard}
                        onChange={(e) => updateRow(idx, { hazard: e.target.value })}
                        className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Who is affected</span>
                      <input
                        type="text"
                        value={r.who_affected}
                        onChange={(e) => updateRow(idx, { who_affected: e.target.value })}
                        className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </label>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => moveRow(idx, -1)}
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-1"
                      title="Move up"
                      disabled={idx === 0}
                    >↑</button>
                    <button
                      type="button"
                      onClick={() => moveRow(idx, 1)}
                      className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-1"
                      title="Move down"
                      disabled={idx === rows.length - 1}
                    >↓</button>
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="text-red-500 hover:text-red-700 dark:hover:text-red-400 px-1 text-sm"
                      title="Remove row"
                      data-testid={`ra-editor-row-remove-${idx}`}
                    >×</button>
                  </div>
                </div>

                <div className="grid gap-3 lg:grid-cols-3">
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Initial HRN</div>
                    <HrnInput
                      values={initial}
                      onChange={(next) => updateRow(idx, {
                        lo_init: next.lo, fe_init: next.fe, dph_init: next.dph, np_init: next.np,
                      })}
                      testIdPrefix={`ra-editor-row-${idx}-init`}
                    />
                  </div>
                  <div className="block">
                    <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Controls</span>
                    <ControlsByStage
                      value={r.controls}
                      onChange={(next) => updateRow(idx, { controls: next })}
                      compact
                    />
                  </div>
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Residual HRN (after controls)</div>
                    <HrnInput
                      values={residual}
                      onChange={(next) => updateRow(idx, {
                        lo_res: next.lo, fe_res: next.fe, dph_res: next.dph, np_res: next.np,
                      })}
                      testIdPrefix={`ra-editor-row-${idx}-res`}
                    />
                  </div>
                </div>

                <label className="block mt-3">
                  <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Notes</span>
                  <input
                    type="text"
                    value={r.notes}
                    onChange={(e) => updateRow(idx, { notes: e.target.value })}
                    className="block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </label>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={addRow}
          className="mt-3 w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
          data-testid="ra-editor-add-row"
        >
          + Add hazard row
        </button>
      </section>

      {/* Sticky action bar */}
      <div className="fixed bottom-0 left-0 right-0 sm:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {isDirty ? (
              <span className="text-amber-600 dark:text-amber-400">● Unsaved changes</span>
            ) : (
              <span>Up to date</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isForkOfSeed && (
              confirmReset ? (
                <>
                  <span className="text-xs text-amber-600 dark:text-amber-400">Reset to CrewForge defaults?</span>
                  <button
                    type="button"
                    onClick={handleReset}
                    className={BTN.warning}
                    disabled={saving}
                    data-testid="ra-editor-reset-confirm"
                  >Yes, reset</button>
                  <button
                    type="button"
                    onClick={() => setConfirmReset(false)}
                    className={BTN.secondary}
                  >Cancel</button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmReset(true)}
                  className={BTN.secondary}
                  disabled={saving}
                  data-testid="ra-editor-reset"
                >Reset to defaults</button>
              )
            )}
            {(forkId || (seedMode && id)) && (
              confirmDelete ? (
                <>
                  <span className="text-xs text-red-600 dark:text-red-400">
                    {seedMode
                      ? 'Delete this CrewForge default? Visible to every customer once deleted.'
                      : 'Delete this block?'}
                  </span>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className={BTN.danger}
                    disabled={saving}
                    data-testid="ra-editor-delete-confirm"
                  >Yes, delete</button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className={BTN.secondary}
                  >Cancel</button>
                </>
              ) : (
                <button
                  type="button"
                  // seedMode skips the inline 2-step confirm because handleDelete
                  // already fires a strong window.confirm warning. Non-seedMode
                  // keeps the existing inline gate.
                  onClick={seedMode ? handleDelete : () => setConfirmDelete(true)}
                  className={BTN['danger-soft']}
                  disabled={saving}
                  data-testid="ra-editor-delete"
                >Delete</button>
              )
            )}
            <button
              type="button"
              onClick={handleSave}
              className={BTN.confirm}
              disabled={saving || !isDirty}
              data-testid="ra-editor-save"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
